import type { Note, MeasureBoundsOptions, MeasureBound, SectionSegment } from "@/types";
import { promiseTimeout } from "@vueuse/core";
import { watch } from "vue";
import { Device } from "@capacitor/device";
import { toLogicalPoint, toLogicalRect } from "./screenRotation";

export const setProperty = (property: string, value: string) => {
  const root = document.documentElement; // gets the :root element
  root.style.setProperty(property, value);
};

// Authoritative iPad check via the native device model (e.g. "iPad13,1").
// On the native app this is reliable; on web/PWA modern iPadOS reports a
// desktop-class model, so this returns false there — matching the rest of
// the app's iPad-specific handling.
export const isIPadDevice = async (): Promise<boolean> => {
  const { model } = await Device.getInfo();
  return model?.toLowerCase().includes("ipad") ?? false;
};

// Authoritative Android tablet check for native and web/PWA.
// On Android browsers, tablets typically report Android without "Mobile".
// On Capacitor, the device platform remains Android, so we also use screen size
// heuristics as a fallback for tablet-like form factors.
export const isAndroidTablet = async (): Promise<boolean> => {
  const info = await Device.getInfo();
  console.log("Device Info:", info);
  if (info.platform !== 'android') return false;

  const ua = typeof navigator !== 'undefined'
    ? navigator.userAgent.toLowerCase()
    : '';

  if (ua.includes('android') && !ua.includes('mobile')) return true;

  const model = info.model?.toLowerCase() ?? '';
  const tabletModelKeywords = ['tab', 'tablet', 'sm-t', 'gt-p', 'nexus 7', 'nexus 9'];
  if (tabletModelKeywords.some((keyword) => model.includes(keyword))) return true;

  const width = Math.min(window.screen.width, window.screen.height);
  return width >= 600;
};


// Helper to check if a time signature is compound
export const isCompoundTime = (numerator: number) => {
  return numerator % 3 === 0 && numerator !== 3;
};

// Helper to avoid floating point precision issues
export const roundToTwoDecimals = (num: number) => {
  return Math.round(num * 100) / 100;
};

export function triggerMouseEvent(targetNode: HTMLElement, eventType: string) {
  const clickEvent = new Event(eventType, { bubbles: true, cancelable: true });
  targetNode.dispatchEvent(clickEvent);
}

export async function withFallbackTimeout<T>(
  promise: Promise<T>,
  fallback: () => T,
  timeoutMs: number,
  label: string
): Promise<T> {
  try {
    const result = await Promise.race([
      promise,
      promiseTimeout(timeoutMs).then(() => {
        return Promise.reject(new Error(`${label} timed out after ${timeoutMs}ms`));
      }),
    ]);
    return result;
  } catch (error) {
    console.warn(`${label} timed out after ${timeoutMs}ms. Using fallback value.`);
    return fallback();
  }
}

export function simulateMouseClick(targetNode: HTMLElement) {
  ["mouseover", "mousedown", "mouseup", "click"].forEach(function (eventType) {
    triggerMouseEvent(targetNode, eventType);
  })
}

// Wait until a condition returns true with graceful timeout fallback.
// Returns a promise that resolves when condition is true or timeout expires.
export const waitUntil = (condition: () => boolean, timeoutMs: number): Promise<void> => {
  return new Promise((resolve) => {
    // Immediate resolution if already true
    if (condition()) {
      resolve();
      return;
    }

    let resolved = false;

    // Watch for condition to become true
    const unwatch = watch(condition, (isReady) => {
      if (isReady && !resolved) {
        resolved = true;
        unwatch();
        resolve();
      }
    });

    // Timeout fallback
    setTimeout(() => {
      if (!resolved) {
        resolved = true;
        unwatch();
        resolve();
      }
    }, timeoutMs);
  });
};

export function findTimeInsertPositionInNotes(notes: Note[], time: number) {
  let left = 0, right = notes.length;
  let mid;
  while (left < right) {
    mid = Math.floor((left + right) / 2);
    if (notes[mid].timestamp < time) {
      left = mid + 1;
    } else {
      right = mid;
    }
  }
  return left;
}

// Grace notes (see Note.isGrace) are still their own hittable notes with their
// own timestamp, but have no business driving the sheet's continuous scroll
// motion — their closely-offset timestamp/x would make the curve visibly jerk
// toward each one on the way to its main note. Shared by every motion/speed
// helper below; hit judging and highlighting elsewhere still read the full,
// unfiltered notes array. Falls back to the unfiltered list if every note
// happens to be a grace note, so callers never end up with nothing to track.
export function excludeGraceNotes(notes: Note[]): Note[] {
  const filtered = notes.filter(note => !note.isGrace);
  return filtered.length ? filtered : notes;
}

/**
 * Average ms of music covered by one pixel of horizontal sheet travel.
 *
 * Measured across the whole piece rather than a local prev/next pair: drum
 * sheets stack several notes on one x (snare + hi-hat on a beat), so a local
 * pair often has dx === 0, and walking outward from a moving anchor would
 * change scale mid-gesture. Pass `scale` when the sheet renders inside a CSS
 * transform to get ms per *screen* pixel.
 *
 * Returns null when the piece is too short or too degenerate to derive a rate.
 */
export function computeMsPerPixel(notes: Note[], scale = 1): number | null {
  const trackedNotes = excludeGraceNotes(notes);
  if (trackedNotes.length < 2) return null;
  const first = trackedNotes[0];
  const last = trackedNotes[trackedNotes.length - 1];
  const dx = last.x - first.x;
  const dt = last.timestamp - first.timestamp;
  if (dx === 0 || dt === 0) return null;
  return dt / dx / (scale || 1);
}

export type SheetScrollPosition = {
  /** Horizontal translation, in local px, that parks `currentTime` on the target line. */
  scrollOffset: number;
  prevNoteIndex: number;
  nextNoteIndex: number;
};

// Fallback px/ms used to extrapolate past the last note when the piece has no
// two notes with distinct timestamps to derive a speed from.
const FALLBACK_SHEET_SPEED_PX_PER_MS = 0.1;

/**
 * Px/ms the sheet was scrolling at by its last note — walks back from the
 * final note to the most recent one with a *different* timestamp (the
 * immediately preceding note often shares one, e.g. stacked drum voices,
 * which would divide by zero). Falls back to a fixed constant when there
 * aren't two distinct timestamps to derive a speed from. Used both to
 * extrapolate scroll position past a piece's last note, and (in multi-line
 * mode) to carry that same trailing speed across a staffline break so the
 * target line's motion reads as continuous rather than a stop-and-jump.
 */
export function getTrailingNoteSpeed(notes: Note[]): number {
  const trackedNotes = excludeGraceNotes(notes);
  if (trackedNotes.length < 2) return FALLBACK_SHEET_SPEED_PX_PER_MS;

  const lastIndex = trackedNotes.length - 1;
  const last = trackedNotes[lastIndex];
  for (let i = lastIndex - 1; i >= 0; i--) {
    if (trackedNotes[i].timestamp < last.timestamp) {
      return (last.x - trackedNotes[i].x) / (last.timestamp - trackedNotes[i].timestamp);
    }
  }

  return FALLBACK_SHEET_SPEED_PX_PER_MS;
}

/**
 * Interpolate the sheet's scroll offset so the music due at `currentTime` sits
 * under the target line, plus the note indices bracketing that moment.
 *
 * Three regimes: before the first note (ease in from `initialScrollOffset`),
 * between two notes (linear interpolation), and past the last note
 * (extrapolate at the trailing speed).
 *
 * Returns null when there are no notes to position against.
 */
export function computeSheetScrollPosition(
  notes: Note[],
  currentTime: number,
  targetPosition: number,
  initialScrollOffset = 0
): SheetScrollPosition | null {
  if (!notes.length) return null;

  // Interpolate against the grace-free subset (see excludeGraceNotes), but
  // report indices into the original `notes` array — callers (e.g.
  // EditMusicAudioSync's highlightNote) index back into what they passed in.
  const trackedNotes = excludeGraceNotes(notes);
  const toOriginalIndex = (trackedIndex: number) => notes.indexOf(trackedNotes[trackedIndex]);

  const insertPosition = findTimeInsertPositionInNotes(trackedNotes, currentTime);

  if (insertPosition === 0) {
    const first = trackedNotes[0];
    return {
      scrollOffset: targetPosition -
        ((currentTime / first.timestamp) * (first.x - initialScrollOffset) + initialScrollOffset),
      prevNoteIndex: toOriginalIndex(0),
      nextNoteIndex: toOriginalIndex(0),
    };
  }

  if (insertPosition < trackedNotes.length) {
    const prevNoteIndex = insertPosition - 1;
    const prev = trackedNotes[prevNoteIndex];
    const next = trackedNotes[insertPosition];
    return {
      scrollOffset: targetPosition -
        ((currentTime - prev.timestamp) / (next.timestamp - prev.timestamp) * (next.x - prev.x) + prev.x),
      prevNoteIndex: toOriginalIndex(prevNoteIndex),
      nextNoteIndex: toOriginalIndex(insertPosition),
    };
  }

  // Past the last note — extrapolate at the trailing speed.
  const lastIndex = trackedNotes.length - 1;
  const last = trackedNotes[lastIndex];
  const speed = getTrailingNoteSpeed(trackedNotes);
  let prevIndex = -1;
  for (let i = lastIndex - 1; i >= 0; i--) {
    if (trackedNotes[i].timestamp < last.timestamp) {
      prevIndex = i;
      break;
    }
  }

  return {
    scrollOffset: targetPosition - (last.x + speed * (currentTime - last.timestamp)),
    prevNoteIndex: toOriginalIndex(prevIndex >= 0 ? prevIndex : lastIndex),
    nextNoteIndex: toOriginalIndex(lastIndex),
  };
}

// VexFlow nests a note's own content (stem/notehead/flag) in a `.vf-note` child
// and its modifiers (accidentals, articulations, an attached grace-note group...)
// in a sibling `.vf-modifiers` group. A note with grace notes attached would
// otherwise report a whole-element rect stretched left to the leftmost grace
// notehead, since those are nested `.vf-stavenote` groups inside `.vf-modifiers`.
// Union the note's own glyphs with every modifier EXCEPT nested grace-note
// groups, so e.g. an accidental still counts toward the note's extent.
const getOwnNoteRect = (el: Element): DOMRect => {
  const ownNoteEl = el.querySelector(':scope > .vf-note');
  if (!ownNoteEl) return el.getBoundingClientRect();

  const rects = [ownNoteEl.getBoundingClientRect()];
  el.querySelectorAll(':scope > .vf-modifiers > :not(.vf-stavenote)').forEach(modifierEl => {
    rects.push(modifierEl.getBoundingClientRect());
  });

  const left = Math.min(...rects.map(r => r.left));
  const top = Math.min(...rects.map(r => r.top));
  const right = Math.max(...rects.map(r => r.right));
  const bottom = Math.max(...rects.map(r => r.bottom));
  return new DOMRect(left, top, right - left, bottom - top);
};

// `.vf-note`'s own rect (getOwnNoteRect) spans the whole glyph — stem and
// flag included — so its top sits well above the actual notehead for an
// up-stemmed note. Annotation placement wants the notehead itself, so this
// drills one level further into the specific `.vf-notehead` sub-element
// (indexed by vfnoteIndex, for chords sharing one stem). Falls back to
// undefined (caller keeps using the whole-glyph rect) if it isn't found.
const getNoteheadRect = (el: Element, vfnoteIndex: number): DOMRect | undefined => {
  const vfnote = el.querySelector(':scope > .vf-note') ?? el;
  const notehead = vfnote.querySelectorAll('.vf-notehead')[vfnoteIndex];
  return notehead?.getBoundingClientRect();
};

export function computeOsmdNoteCoordinates(
  notes: Note[],
  sheetEl: HTMLElement,
  scale = 1
) {
  // notes should be pre-sorted by timestamp and have their graphicalNote property set
  // We compute the x coordinate of each note by comparing the bounding rect of its graphical element to the sheet's bounding rect.
  // Rects are screen-space, so both go through toLogicalRect — the sheet's axis is
  // not the screen's while the app is rotated for mobile portrait.
  const sheetRect = toLogicalRect(sheetEl.getBoundingClientRect());
  const sheetLeft = sheetRect.left;
  const sheetTop = sheetRect.top;

  // Notes owning a notehead on the page. Tremolo repeats are held back: they share
  // the parent's graphical note, so its rect would pin a whole roll to one x, and
  // computeSheetScrollPosition — which interpolates the scroll between consecutive
  // notes' x — would freeze the sheet there and then jump.
  const anchors: number[] = [];
  for (const [index, note] of notes.entries()) {
    if (note.isTremoloRepeat) continue;

    const graphicalNote = note.graphicalNote as { getSVGGElement?: () => Element | null; vfnoteIndex?: number } | undefined;
    const noteEl = graphicalNote?.getSVGGElement?.();
    const noteRect = noteEl ? getOwnNoteRect(noteEl) : undefined;
    if (!noteRect) continue;

    const previous = notes[anchors[anchors.length - 1]];
    const logicalNoteRect = toLogicalRect(noteRect);
    note.x = previous?.timestamp === note.timestamp
      ? previous.x
      : (logicalNoteRect.left - sheetLeft) / scale;
    // Vertical position doesn't affect scroll/timing (only x does), so it's
    // simply read straight off the notehead's own rect (see getNoteheadRect
    // above) — used solely to place annotation text directly above the note
    // (see useNoteAnnotations). Falls back to the whole-glyph rect if the
    // notehead sub-element can't be found. Center, not top, of that rect —
    // top is the oval's upper edge, which still reads as "a bit too high"
    // relative to where the notehead actually sits on the staff.
    const noteheadRect = noteEl ? getNoteheadRect(noteEl, graphicalNote?.vfnoteIndex ?? 0) : undefined;
    const logicalNoteheadRect = noteheadRect ? toLogicalRect(noteheadRect) : logicalNoteRect;
    note.y = (logicalNoteheadRect.top + logicalNoteheadRect.height / 2 - sheetTop) / scale;
    anchors.push(index);
  }

  // Spread each roll's repeats across the gap to the next notehead by timestamp, so
  // the scroll runs through the roll at the rate it would have without it.
  let anchorCursor = 0;
  for (const [index, note] of notes.entries()) {
    if (!note.isTremoloRepeat) continue;
    while (anchorCursor < anchors.length && anchors[anchorCursor] < index) anchorCursor++;

    const prev = notes[anchors[anchorCursor - 1]];
    if (!prev) continue;
    const next = notes[anchors[anchorCursor]];
    // A roll shares its parent's pitch throughout, so the vertical position
    // never changes across the repeats — only x is interpolated below.
    note.y = prev.y;

    if (next && next.timestamp > prev.timestamp) {
      note.x = prev.x + (note.timestamp - prev.timestamp) / (next.timestamp - prev.timestamp) * (next.x - prev.x);
      continue;
    }

    // A roll running to the end of the piece has no notehead ahead to aim at, so
    // carry forward the spacing it arrived at rather than stalling on the last one.
    let beforeCursor = anchorCursor - 2;
    while (beforeCursor >= 0 && notes[anchors[beforeCursor]].timestamp === prev.timestamp) beforeCursor--;
    const before = notes[anchors[beforeCursor]];
    const pixelsPerMs = before ? (prev.x - before.x) / (prev.timestamp - before.timestamp) : 0;
    note.x = prev.x + pixelsPerMs * (note.timestamp - prev.timestamp);
  }
}

/**
 * Reads a MusicXML or MXL file into a string suitable for passing to OSMD's load().
 * MXL files are ZIP-compressed and must be read as ArrayBuffer + converted to binary string.
 * XML/MusicXML files can be read as plain text.
 */
export function readMusicXmlFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const name = file.name.toLowerCase();
    const isMxl = name.endsWith('.mxl');
    const isXml = name.endsWith('.xml') || name.endsWith('.musicxml');
    if (!isMxl && !isXml) {
      reject(new Error('No valid .xml/.mxl/.musicxml file!'));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.onload = (e) => {
      try {
        const result = e.target!.result;
        if (result instanceof ArrayBuffer) {
          const bytes = new Uint8Array(result);
          let binary = '';
          for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
          resolve(binary);
        } else {
          resolve(result as string);
        }
      } catch (err) {
        reject(err);
      }
    };
    isMxl ? reader.readAsArrayBuffer(file) : reader.readAsText(file);
  });
}

const normalizeColor = (color: string) => {
  const temp = document.createElement('div');
  temp.style.color = color;
  // We don't need to append to body to get the computed value for basic colors
  return temp.style.color.replace(/\s+/g, ''); // Remove spaces for strict matching
};

const colorCache: Map<string, string> = new Map();
export const memoizedNormalizeColor = (color: string) => {
  if (colorCache.has(color)) return colorCache.get(color) as string;
  const normalized = normalizeColor(color);
  colorCache.set(color, normalized);
  return normalized;
};

export function hexToRgb(hex: string): number[] {
  if (!hex || typeof hex !== 'string') {
    throw new Error('Invalid hex color: must be a non-empty string');
  }
  hex = hex.replace(/^#/, '');
  if (!/^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(hex)) {
    throw new Error('Invalid hex color format: must be 3 or 6 characters');
  }
  if (hex.length === 3) {
    hex = hex.split('').map(x => x + x).join('');
  }
  const int = parseInt(hex, 16);
  return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
}

/**
 * Converts a hex color to rgba string with specified alpha value
 */
export function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export function getLuminance(rgb: number[]): number {
  const [r, g, b] = rgb.map((v) => {
    v /= 255;
    return v <= 0.03928
      ? v / 12.92
      : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function getContrastColor(bgHex: string): string {
  const bgRgb = hexToRgb(bgHex);
  const bgLuminance = getLuminance(bgRgb);

  // Luminance of black and white
  const whiteLuminance = 1;
  const blackLuminance = 0;

  const contrastWithWhite = (Math.max(bgLuminance, whiteLuminance) + 0.05) /
    (Math.min(bgLuminance, whiteLuminance) + 0.05);
  const contrastWithBlack = (Math.max(bgLuminance, blackLuminance) + 0.05) /
    (Math.min(bgLuminance, blackLuminance) + 0.05);

  return contrastWithWhite > contrastWithBlack ? 'var(--color-white)' : 'var(--color-black)';
}

export function recolorOsmdByTheme(target: HTMLElement | undefined, theme: string) {
  setTimeout(() => {
    if (!target) return;
    const styles = getComputedStyle(document.documentElement);
    const black = memoizedNormalizeColor(styles.getPropertyValue('--color-black').trim());
    const white = memoizedNormalizeColor(styles.getPropertyValue('--color-white').trim());

    if (theme === 'dark') {
      target.querySelectorAll('path, rect, text').forEach((element) => {
        if (memoizedNormalizeColor(element.getAttribute('stroke')?.toLowerCase() || '') === black) {
          element.setAttribute('stroke', white);
        } else if (memoizedNormalizeColor(element.getAttribute('fill')?.toLowerCase() || '') === black) {
          element.setAttribute('fill', white);
        }
      });
    } else {
      target.querySelectorAll('path, rect, text').forEach((element) => {
        if (memoizedNormalizeColor(element.getAttribute('stroke')?.toLowerCase() || '') === white) {
          element.setAttribute('stroke', black);
        } else if (memoizedNormalizeColor(element.getAttribute('fill')?.toLowerCase() || '') === white) {
          element.setAttribute('fill', black);
        }
      });
    }
  });
}

/**
 * Calculate the x position and width of a section based on its measure bounds.
 * Returns null if bounds are empty or section bounds not found.
 */
export function getSectionBounds(
  section: { startMeasureIndex: number; endMeasureIndex: number },
  measureBounds: MeasureBound[]
): { x: number; width: number } | null {
  if (!measureBounds.length) return null;
  const start = getMeasureBoundForIndex(measureBounds, section.startMeasureIndex);
  const end = getMeasureBoundForIndex(measureBounds, section.endMeasureIndex);
  if (!start || !end) return null;
  const x = start.x;
  const width = Math.max(0, end.x + end.width - x);
  return { x, width };
}

/**
 * Resolve a visible measure bound from a source-measure index.
 * Handles collapsed/rest-combined systems where some source measures are not rendered.
 */
export function getMeasureBoundForIndex(
  measureBounds: MeasureBound[],
  measureIndex: number
): MeasureBound | undefined {
  if (!measureBounds.length) return;

  const exact = measureBounds.find((b) => b.measureIndex === measureIndex);
  if (exact) return exact;

  let prev: MeasureBound | undefined;
  let next: MeasureBound | undefined;
  for (const bound of measureBounds) {
    if (bound.measureIndex < measureIndex) prev = bound;
    if (bound.measureIndex > measureIndex) {
      next = bound;
      break;
    }
  }

  // If there is a source-index gap between prev and next, this index is part of
  // a visually collapsed span; map to the previous visible bound.
  if (prev && next && next.measureIndex - prev.measureIndex > 1) return prev;

  return prev ?? next;
}

type MeasureAtPointOptions = {
  scrollLeft?: number;
  scrollTop?: number;
};

export function getMeasureAtPoint(
  measureBounds: MeasureBound[],
  containerRect: DOMRect | DOMRectReadOnly,
  clientX: number,
  clientY: number,
  options: MeasureAtPointOptions = {}
): MeasureBound | null {
  const point = toLogicalPoint(clientX, clientY);
  const logicalRect = toLogicalRect(containerRect as DOMRect);
  const localX = point.x - logicalRect.left + (options.scrollLeft ?? 0);
  const localY = point.y - logicalRect.top + (options.scrollTop ?? 0);

  let closestInRow: MeasureBound | undefined;
  let closestInRowDx = Infinity;
  let closestAny: MeasureBound | undefined;
  let closestAnyRectDist = Infinity;

  for (const mb of measureBounds) {
    if (mb.y === undefined || mb.height === undefined) continue;

    const right = mb.x + mb.width;
    const bottom = mb.y + mb.height;
    const dx = localX < mb.x ? mb.x - localX : localX > right ? localX - right : 0;
    const dy = localY < mb.y ? mb.y - localY : localY > bottom ? localY - bottom : 0;
    const rectDist = dx + dy;

    if (rectDist < closestAnyRectDist) {
      closestAnyRectDist = rectDist;
      closestAny = mb;
    }

    if (localY < mb.y || localY > bottom) continue;

    const rowDx = Math.abs(localX - (mb.x + mb.width / 2));
    if (rowDx < closestInRowDx) {
      closestInRowDx = rowDx;
      closestInRow = mb;
    }
  }

  return closestInRow ?? closestAny ?? null;
}

export function getSectionSegments(
  section: { startMeasureIndex: number; endMeasureIndex: number },
  measureBounds: MeasureBound[]
): SectionSegment[] {
  const bounds = measureBounds.filter(
    (b) => b.measureIndex >= section.startMeasureIndex && b.measureIndex <= section.endMeasureIndex
  );
  if (!bounds.length) return [];

  const rows = new Map<number, MeasureBound[]>();
  bounds.forEach((bound) => {
    const rowKey = bound.rowGroup ?? 0;
    if (!rows.has(rowKey)) rows.set(rowKey, []);
    rows.get(rowKey)!.push(bound);
  });

  const sortedRows = [...rows.entries()].sort((a, b) => a[0] - b[0]);
  return sortedRows.flatMap(([, rowBounds], index) => {
    const yValues = rowBounds.map((bound) => bound.y).filter((value): value is number => value !== undefined);
    const heightValues = rowBounds
      .map((bound) => bound.height)
      .filter((value): value is number => value !== undefined);
    if (!yValues.length || !heightValues.length) return [];

    const minX = Math.min(...rowBounds.map((bound) => bound.x));
    const maxX = Math.max(...rowBounds.map((bound) => bound.x + bound.width));
    const minY = Math.min(...yValues);
    const maxY = Math.max(...rowBounds.map((bound) => (bound.y ?? minY) + (bound.height ?? 0)));

    return [{
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
      isFirst: index === 0,
      isLast: index === sortedRows.length - 1,
    }];
  });
}

/** A 5-line staff spans exactly 4 OSMD units (1 unit per line gap). */
const STAFF_HEIGHT_UNITS = 4;

/**
 * Compute measure bounds from an OSMD instance.
 * Used for sections overlay, loop selection, and measure interactions.
 */
export function computeOsmdMeasureBounds(options: MeasureBoundsOptions): MeasureBound[] {
  const {
    osmd,
    osmdEl,
    includeVertical = true,
    includeRowGrouping = false,
    sectionPaddingY = 0,
  } = options;

  if (!osmd || !osmdEl) return [];

  const svg = osmdEl.querySelector('svg');
  if (!svg) return [];

  const svgRect = svg.getBoundingClientRect();
  // osmdEl is the sole coordinate frame: measuring relative to its own rect
  // (instead of a separate scroll container + scrollLeft/scrollTop correction)
  // is scroll-invariant for free, since osmdEl moves together with its own content.
  const osmdElRect = osmdEl.getBoundingClientRect();

  const offsetX = svgRect.left - osmdElRect.left;
  const offsetY = svgRect.top - osmdElRect.top;

  // Per OSMD wiki: AbsolutePosition is in OSMD units where 1 unit = 10px at zoom=1
  const unitToPx = 10 * osmd.zoom;

  // VexFlowMeasure.draw() tags every rendered measure's own group with this class
  // and id=MeasureNumber — the real rendered box, unlike PositionAndShape below
  // (OSMD's internal width *estimate*, which a custom calculateMeasureXLayout
  // override can skew away from what's actually drawn). Measuring the DOM directly
  // keeps x/width accurate the same way computeOsmdNoteCoordinates measures notes.
  const measureGroupsByNumber = new Map<string, Element>();
  svg.querySelectorAll('g.vf-measure').forEach((el) => {
    if (el.id) measureGroupsByNumber.set(el.id, el);
  });

  const measureList = osmd.GraphicSheet?.MeasureList;
  if (!measureList) return [];

  const measureCount = measureList.length;
  if (!measureCount) return [];

  const rawBounds: MeasureBound[] = [];
  let prevY = -1;
  let rowGroup = -1;
  const ROW_THRESHOLD = 2; // OSMD units — gap between systems is much larger

  const resolveSourceMeasureIndex = (measure: Record<string, unknown>, fallback: number): number => {
    // measureInput's shape varies across OSMD versions/graphic-measure types, so we
    // probe a list of known property-name variants rather than relying on a single typed field.
    const candidates = [
      measure?.MeasureNumber,
      measure?.measureNumber,
      measure?.ParentSourceMeasure?.MeasureNumber,
      measure?.parentSourceMeasure?.MeasureNumber,
      measure?.SourceMeasure?.measureNumber,
      measure?.sourceMeasure?.measureNumber,
      measure?.SourceMeasure?.MeasureNumber,
      measure?.sourceMeasure?.MeasureNumber,
      measure?.sourceMeasureIndex != null ? Number(measure.sourceMeasureIndex) + 1 : undefined,
    ];
    for (const n of candidates) {
      if (typeof n === 'number' && Number.isFinite(n) && n >= 1) {
        return Math.max(0, Math.floor(n) - 1);
      }
    }
    return fallback;
  };

  for (let mi = 0; mi < measureCount; mi++) {
    const measure = measureList[mi]?.[0];
    if (!measure?.PositionAndShape) continue;

    const pos = measure.PositionAndShape.AbsolutePosition;
    const size = measure.PositionAndShape.Size;

    const sourceMeasureIndex = resolveSourceMeasureIndex(measure, mi);

    const measureGroupRect = measureGroupsByNumber.get(String(measure.MeasureNumber))?.getBoundingClientRect();

    const bound: MeasureBound = measureGroupRect
      ? {
        measureIndex: sourceMeasureIndex,
        x: measureGroupRect.left - osmdElRect.left,
        width: measureGroupRect.width,
      }
      : {
        // No rendered group (e.g. a collapsed multi-rest placeholder) — fall back
        // to OSMD's internal estimate rather than dropping the measure entirely.
        measureIndex: sourceMeasureIndex,
        x: offsetX + pos.x * unitToPx,
        width: size.width * unitToPx,
      };

    if (includeVertical) {
      // A measure's box is sized to its contents, not the staff: a rest-only bar reports
      // ~1 unit against the staff's 4, collapsing its overlay and hit target. The parent
      // staffline gives one band for the whole system — but its box is content-derived
      // too, so alone it covers neither the staff nor the notes. AbsolutePosition.y is
      // the top staff line; BorderTop/Size reach out to beams and stems. Union both.
      const box = measure.ParentStaffLine?.PositionAndShape ?? measure.PositionAndShape;
      const staffTop = box.AbsolutePosition.y;
      const contentTop = staffTop + box.BorderTop;
      const top = Math.min(staffTop, contentTop);
      const bottom = Math.max(staffTop + STAFF_HEIGHT_UNITS, contentTop + box.Size.height);

      bound.y = offsetY + top * unitToPx - sectionPaddingY;
      bound.height = (bottom - top) * unitToPx + sectionPaddingY * 2;
    }

    if (includeRowGrouping) {
      if (prevY < 0 || Math.abs(pos.y - prevY) > ROW_THRESHOLD) {
        rowGroup++;
        prevY = pos.y;
      }
      bound.rowGroup = rowGroup;
    }

    rawBounds.push(bound);
  }

  if (!rawBounds.length) return [];

  rawBounds.sort((a, b) => a.measureIndex - b.measureIndex);

  // De-duplicate same source index (can happen with alternate render paths).
  const deduped: MeasureBound[] = [];
  for (const b of rawBounds) {
    const last = deduped[deduped.length - 1];
    if (last?.measureIndex === b.measureIndex) continue;
    deduped.push(b);
  }

  // Fill hidden source-measure gaps (e.g. multi-rest compression) by reusing the
  // previous visible bound so index-based consumers remain aligned with source indexes.
  const expanded: MeasureBound[] = [];
  for (let i = 0; i < deduped.length; i++) {
    const current = deduped[i];
    expanded.push(current);
    const next = deduped[i + 1];
    if (!next) continue;
    for (let missing = current.measureIndex + 1; missing < next.measureIndex; missing++) {
      expanded.push({ ...current, measureIndex: missing });
    }
  }

  return expanded;
}